// KPI store — Zustand (domain başına store kuralı; M16 T9). Seçim (clientId/setId) ekranlar arası
// globaldir ve kullanıcı bazında localStorage'da kalıcıdır (kural 19/K30: varsayılan müşteri YOK).
// `hydrate` query > localStorage önceliğiyle çözer; geçersiz seçimi sessizce temizler.
// `saveCell` optimistik yazar; hata/409'da eski hücreye döner (rollback), ardından `refreshGrid`.
// `saveCells` (TODO-KPI-05 yapıştırma) aynı yazımı SIRAYLA tekil PUT ile koşar, sunucu hesabını
// (refreshGrid) toplu işin SONUNDA bir kez ister — backend'de toplu uç yok.
// Türetilmiş hücreye yazım FE'de de engellenir (isDerived guard — BE 400 KPI_DERIVED_READONLY).
// D20: her yazım göstergenin birimini beyan eder; 409 KPI_UNIT_CHANGED → geri alma + yerel birim.
// Kırılım (spec §6.2 DR4): `pendingCells` (hücre anahtarı → bekleyen yazım) grid yenilenene kadar
// tutulur — toplam hücresi `selectTotalRecomputing` ile soluk + aria-busy bekler; sunucunun
// KPI_DERIVED_READONLY / KPI_GROUP_READONLY reddi kullanıcı metnine (`readonlyHintFor`) çevrilir.

import { create } from "zustand";
import type {
  KpiResolvedCell,
  KpiResolvedGrid,
  KpiValueStatus,
  SetFillStatus,
} from "@mahir/shared";
import { ApiError } from "../api/client";
import {
  getKpiResolved,
  getKpiSet,
  listKpiClientsSummary,
  listKpiSets,
  putKpiValue,
  type KpiClientSummary,
  type KpiSetWithCount,
  type KpiValueDto,
} from "../api/kpi";
import {
  defaultKpiYears,
  KPI_COPY,
  pickSetForClient,
  readKpiSelection,
  resolveKpiHydration,
  writeKpiSelection,
} from "../lib/kpi-ui";
import {
  isWriteBlockedIndicator,
  readonlyHintFor,
  totalRecomputing,
} from "../lib/kpi-grid-breakdown";
import type { GridColumn } from "../lib/kpi-grid-nav";
import { KPI_BREAKDOWN_COPY } from "../lib/kpi-breakdown-copy";
import { patchIndicatorUnit, unitChangedCurrent } from "../lib/kpi-unit-changed";
import { KPI_UNIT_COPY } from "../lib/kpi-unit-copy";
import { createSetBreakdownSlice, type KpiSetBreakdownState } from "./kpi-store-set-breakdown";

/** güvenlik/UX taraması 2026-10-08 (U-M1) — 409 KPI_STALE_WRITE: sunucudaki değer diyaloğa gider */
export interface KpiStaleConflict {
  key: string;
  current: KpiValueDto | null;
  message: string;
}

export interface SaveCellArgs {
  metricId: string;
  entityId: string;
  year: number;
  status: KpiValueStatus;
  numericValue?: number;
  textValue?: string;
  note?: string;
  footnote?: string;
  /** M17 — onaylı değeri değiştirirken neden (raporda "Düzeltmeler" dipnotu) */
  changeReason?: string;
  /**
   * D20 — yazarın değeri yazdığı andaki birim (toplu yapıştırma başında yakalanır; "Tekrar dene"
   * kuyruğundaki girdi ilk yazımın birimini taşır). Yoksa göstergenin o anki birimi.
   */
  declaredUnit?: string | null;
}

type WriteResult = "ok" | "failed" | "unit_changed";

export interface KpiStoreState extends KpiSetBreakdownState {
  userId: string | null;
  clientId: string | null;
  setId: string | null;
  years: number[];
  entityIds: string[];
  /**
   * Müşteri özeti — ClientPicker işi (2026-10-07): sunucudan ad sırasıyla İLK 50 + seçili /
   * sonradan seçilen müşteriler (`ensureClient` ile eklenir). Tam liste DEĞİL; arama picker
   * üzerinden sunucuda yapılır. `clientsTotal` toplam sayıyı taşır (boş durum "N daha").
   */
  clients: KpiClientSummary[];
  clientsTotal: number;
  sets: KpiSetWithCount[];
  grid: KpiResolvedGrid | null;
  fill: SetFillStatus | null;
  cycle: string[] | null;
  loading: boolean;
  gridLoading: boolean;
  error: string | null;
  /** kaydedilmemiş hücre sayısı (kural 9 — ContextBar kilidi) */
  pendingSaves: number;
  /** DR4 — bekleyen yazımlar hücre bazında (`metricId|entityId|year` → adet), grid yenilenene kadar */
  pendingCells: Record<string, number>;
  saveErrors: Record<string, string>;
  /**
   * güvenlik/UX taraması 2026-10-08 (U-H3) — kaydedilemeyen hücrelerin YAZILAN girdisi; "Tekrar
   * dene" bunları yeniden gönderir (refreshGrid yazılanı silerdi). 409 stale burada tutulmaz.
   */
  failedInputs: Record<string, SaveCellArgs>;
  /** güvenlik/UX taraması 2026-10-08 (U-M1) — 409 stale-write diyaloğu (sunucudaki değerle) */
  staleConflict: KpiStaleConflict | null;
  lastSavedAt: string | null;

  hydrate: (args: {
    userId: string;
    query?: { clientId?: string; setId?: string };
  }) => Promise<void>;
  loadClients: () => Promise<void>;
  /** `clients` listesinde yoksa tekil özetle ekler (seçim / son kullanılan / ?clientId). */
  ensureClient: (clientId: string) => Promise<KpiClientSummary | null>;
  selectClient: (clientId: string | null) => Promise<void>;
  selectSet: (setId: string | null) => Promise<void>;
  setYears: (years: number[]) => Promise<void>;
  refreshGrid: () => Promise<void>;
  /** set listesi tazelenir, seçim korunur (set editörü ekle/kaldır sonrası) */
  reloadSets: () => Promise<void>;
  saveCell: (args: SaveCellArgs) => Promise<boolean>;
  /** toplu yapıştırma: sırayla tekil PUT, tek refreshGrid; `failed` hücreler `saveErrors`'ta */
  saveCells: (items: SaveCellArgs[]) => Promise<{ ok: number; failed: number }>;
  /** U-H3 — kaydedilemeyen hücrelerin yazılan girdisini yeniden gönderir (tek refreshGrid) */
  retryFailed: () => Promise<{ ok: number; failed: number }>;
  /** U-H3 — yazılanı bırak, sunucudaki değerleri yükle */
  discardFailed: () => Promise<void>;
  /** U-M1 — stale diyaloğu kapatılır; `refresh` ile sunucudaki değer tabloya gelir */
  acknowledgeStale: (refresh: boolean) => Promise<void>;
  clearSelection: () => void;
}

function cellKey(metricId: string, entityId: string, year: number): string {
  return `${metricId}|${entityId}|${year}`;
}

/** Bekleyen hücre sayacı: +1 / −1 (0'a inen anahtar silinir). */
function bumpPending(
  pending: Readonly<Record<string, number>>,
  keys: readonly string[],
  delta: 1 | -1,
): Record<string, number> {
  const next = { ...pending };
  for (const k of keys) {
    const n = (next[k] ?? 0) + delta;
    if (n > 0) next[k] = n;
    else delete next[k];
  }
  return next;
}

/** Sunucu yazım reddinin kullanıcı metni — kırılım toplamı/grup satırı kodları ortak kopyaya. */
const READONLY_CODES: ReadonlySet<string> = new Set(["KPI_DERIVED_READONLY", "KPI_GROUP_READONLY"]);

/**
 * DR4 bekleme seçicisi: toplam satırının (indicator.id) bu kolondaki hücresi, hesap grubu üyesinin
 * kaydı sürerken (grid yenilemesi gelene kadar) meşgul. `useKpiStore(selectTotalRecomputing(…))`.
 */
export function selectTotalRecomputing(
  totalId: string,
  col: Pick<GridColumn, "entityId" | "year" | "aggregate">,
): (s: Pick<KpiStoreState, "grid" | "pendingCells">) => boolean {
  return (s) => {
    const total = s.grid?.indicators.find((i) => i.id === totalId);
    return !!total && !!s.grid && totalRecomputing(s.pendingCells, total, s.grid.indicators, col);
  };
}

function persist(state: Pick<KpiStoreState, "userId" | "clientId" | "setId" | "years">): void {
  if (state.userId)
    writeKpiSelection(state.userId, {
      clientId: state.clientId,
      setId: state.setId,
      years: state.years,
    });
}

/** Yıl penceresi: kullanıcı daha önce seçtiyse o; yoksa setin veri yıllarından varsayılan. */
function initialYearsFor(
  userId: string | null,
  sets: readonly KpiSetWithCount[],
  setId: string | null,
): number[] {
  const stored = userId ? readKpiSelection(userId) : { clientId: null, setId: null };
  if (stored.years && stored.years.length > 0) return stored.years;
  const dataYears = sets.find((x) => x.id === setId)?.dataYears ?? [];
  return defaultKpiYears(new Date().getFullYear(), 4, dataYears);
}

function patchGridCell(
  grid: KpiResolvedGrid,
  metricId: string,
  entityId: string,
  year: number,
  patch: (prev: KpiResolvedCell | undefined) => KpiResolvedCell,
): KpiResolvedGrid {
  return {
    ...grid,
    indicators: grid.indicators.map((ind) => {
      if (ind.metricId !== metricId) return ind;
      const byYear = { ...(ind.cells[entityId] ?? {}) };
      byYear[year] = patch(byYear[year]);
      return { ...ind, cells: { ...ind.cells, [entityId]: byYear } };
    }),
  };
}

/** Aynı id tekilleşir (yeni kayıt kazanır), ad sırası (tr) korunur. */
function mergeClients(
  incoming: readonly KpiClientSummary[],
  current: readonly KpiClientSummary[],
): KpiClientSummary[] {
  const byId = new Map<string, KpiClientSummary>();
  for (const c of current) byId.set(c.id, c);
  for (const c of incoming) byId.set(c.id, c);
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name, "tr"));
}

export const useKpiStore = create<KpiStoreState>()((set, get) => {
  const writeCell = async (args: SaveCellArgs, refresh: boolean): Promise<WriteResult> => {
    const { grid } = get();
    if (!grid) return "failed";
    const ind = grid.indicators.find((i) => i.metricId === args.metricId);
    // isDerived guard + kırılım: hesap gruplu toplam / grup / kategori satırı yazılmaz (üye kaydı
    // toplamı asla hedeflemez — BE 400 KPI_DERIVED_READONLY / KPI_GROUP_READONLY)
    if (!ind || isWriteBlockedIndicator(ind, grid.indicators)) return "failed";
    const unit = args.declaredUnit !== undefined ? args.declaredUnit : (ind.unit ?? null);
    const key = cellKey(args.metricId, args.entityId, args.year);
    const prev = ind.cells[args.entityId]?.[args.year];
    // optimistik
    set({
      pendingSaves: get().pendingSaves + 1,
      pendingCells: bumpPending(get().pendingCells, [key], 1),
      grid: patchGridCell(grid, args.metricId, args.entityId, args.year, (p) => ({
        ...(p ?? { status: "empty" as const, isDerived: false }),
        status: args.status,
        ...(args.numericValue !== undefined ? { value: args.numericValue } : { value: undefined }),
        ...(args.textValue !== undefined ? { text: args.textValue } : { text: undefined }),
      })),
    });
    try {
      const saved = await putKpiValue({
        metricId: args.metricId,
        entityId: args.entityId,
        year: args.year,
        periodType: "annual",
        status: args.status,
        unit,
        ...(args.numericValue !== undefined ? { numericValue: args.numericValue } : {}),
        ...(args.textValue !== undefined ? { textValue: args.textValue } : {}),
        ...(args.note !== undefined ? { note: args.note } : {}),
        ...(args.footnote !== undefined ? { footnote: args.footnote } : {}),
        ...(args.changeReason !== undefined ? { changeReason: args.changeReason } : {}),
        ...(prev?.updatedAt ? { clientUpdatedAt: prev.updatedAt } : {}),
      });
      const errs = { ...get().saveErrors };
      delete errs[key];
      const failed = { ...get().failedInputs };
      delete failed[key];
      set({ saveErrors: errs, failedInputs: failed, lastSavedAt: saved.updatedAt });
      const g = get().grid;
      if (g) {
        set({
          grid: patchGridCell(g, args.metricId, args.entityId, args.year, (p) => ({
            ...(p ?? { status: "empty" as const, isDerived: false }),
            valueId: saved.id,
            version: saved.version,
            updatedAt: saved.updatedAt,
            updatedBy: saved.updatedBy,
            workflowState: saved.workflowState as NonNullable<KpiResolvedCell["workflowState"]>,
          })),
        });
      }
      // türetilmiş/konsolide hücreler için sunucu hesabı (kural 5 lokal recompute T10'da)
      if (refresh) await get().refreshGrid();
      return "ok";
    } catch (err) {
      const stale = err instanceof ApiError && err.code === "KPI_STALE_WRITE";
      const readonlyHint =
        err instanceof ApiError && READONLY_CODES.has(err.code)
          ? (readonlyHintFor(ind, grid.indicators, false) ?? KPI_BREAKDOWN_COPY.readonlyHint(null))
          : null;
      const msg = stale
        ? KPI_COPY.stale.inline
        : (readonlyHint ?? (err instanceof Error ? err.message : KPI_COPY.store.saveFailed));
      const g = get().grid;
      const rolledGrid = g
        ? patchGridCell(
            g,
            args.metricId,
            args.entityId,
            args.year,
            () => prev ?? { status: "empty", isDerived: false },
          )
        : null;
      const rolled = rolledGrid ? { grid: rolledGrid } : {};
      const unitNow =
        err instanceof ApiError && err.code === "KPI_UNIT_CHANGED"
          ? unitChangedCurrent(err.details)
          : undefined;
      if (unitNow !== undefined && rolledGrid) {
        // D20: sunucu yazmadı; birim yerelde güncellenir (load yok), eski birimli sayı "Tekrar dene"
        // kuyruğuna girmez — yeni birimle yeniden gönderilmesin. I2: komşu hücreler çevrilmiş
        // sayılarla gelsin diye yalnız değerler yenilenir (refreshGrid; hata satırı/taslak kalır) —
        // toplu yazım kendi sonunda tek refreshGrid ister
        const failed = { ...get().failedInputs };
        delete failed[key];
        set({
          grid: patchIndicatorUnit(rolledGrid, args.metricId, unitNow),
          saveErrors: {
            ...get().saveErrors,
            [key]: KPI_UNIT_COPY.writeUnitChanged(unitNow ?? KPI_UNIT_COPY.unitless),
          },
          failedInputs: failed,
        });
        if (refresh) await get().refreshGrid();
        // `pendingCells` bu dalda da `finally`'de bırakılır (toplam beklemede asılı kalmaz)
        return "unit_changed";
      } else if (stale) {
        // güvenlik/UX taraması 2026-10-08 (U-M1): rollback + diyalog (sunucudaki değer `details.current`)
        const current =
          err.details && typeof err.details === "object"
            ? ((err.details as { current?: KpiValueDto }).current ?? null)
            : null;
        set({ ...rolled, staleConflict: { key, current, message: msg } });
      } else {
        // güvenlik/UX taraması 2026-10-08 (U-H3): yazılan girdi saklanır → "Tekrar dene" yeniden gönderir
        set({
          ...rolled,
          saveErrors: { ...get().saveErrors, [key]: msg },
          failedInputs: { ...get().failedInputs, [key]: { ...args, declaredUnit: unit } },
        });
      }
      return "failed";
    } finally {
      set({
        pendingSaves: Math.max(0, get().pendingSaves - 1),
        pendingCells: bumpPending(get().pendingCells, [key], -1),
      });
    }
  };

  /**
   * Toplu yazım (yapıştırma / Tekrar dene): birimler BAŞTA bir kez yakalanır — bir göstergenin ilk
   * hücresi 409 KPI_UNIT_CHANGED alınca yerel birim değişir; kalan hücreleri (eski birimde yazıldı)
   * yeni birimle göndermek sessiz 1000× hata olurdu. O göstergenin kalanları gönderilmez, başarısız
   * sayılır ve "Tekrar dene" kuyruğuna girmez.
   */
  const writeBatch = async (
    items: readonly SaveCellArgs[],
  ): Promise<{ ok: number; failed: number }> => {
    const units = new Map((get().grid?.indicators ?? []).map((i) => [i.metricId, i.unit ?? null]));
    const blocked = new Set<string>();
    let ok = 0;
    // DR4: toplu yazımda hücreler tek refreshGrid'e kadar bekler (toplam o ana kadar meşgul)
    const held = items.map((i) => cellKey(i.metricId, i.entityId, i.year));
    set({ pendingCells: bumpPending(get().pendingCells, held, 1) });
    for (const item of items) {
      if (blocked.has(item.metricId)) {
        const key = cellKey(item.metricId, item.entityId, item.year);
        const now = get().grid?.indicators.find((i) => i.metricId === item.metricId)?.unit ?? null;
        const failed = { ...get().failedInputs };
        delete failed[key];
        set({
          saveErrors: {
            ...get().saveErrors,
            [key]: KPI_UNIT_COPY.writeUnitChanged(now ?? KPI_UNIT_COPY.unitless),
          },
          failedInputs: failed,
        });
        continue;
      }
      const declaredUnit =
        item.declaredUnit !== undefined ? item.declaredUnit : (units.get(item.metricId) ?? null);
      const r = await writeCell({ ...item, declaredUnit }, false);
      if (r === "ok") ok += 1;
      else if (r === "unit_changed") blocked.add(item.metricId);
    }
    try {
      if (items.length > 0) await get().refreshGrid();
    } finally {
      set({ pendingCells: bumpPending(get().pendingCells, held, -1) });
    }
    return { ok, failed: items.length - ok };
  };

  return {
    userId: null,
    clientId: null,
    setId: null,
    years: defaultKpiYears(),
    entityIds: [],
    clients: [],
    clientsTotal: 0,
    sets: [],
    grid: null,
    fill: null,
    cycle: null,
    loading: false,
    gridLoading: false,
    error: null,
    pendingSaves: 0,
    pendingCells: {},
    saveErrors: {},
    failedInputs: {},
    staleConflict: null,
    lastSavedAt: null,
    ...createSetBreakdownSlice(set, get),

    async loadClients() {
      const res = await listKpiClientsSummary();
      set({ clients: mergeClients(res.clients, get().clients), clientsTotal: res.total });
    },

    async ensureClient(clientId) {
      const have = get().clients.find((c) => c.id === clientId);
      if (have) return have;
      try {
        const res = await listKpiClientsSummary({ ids: [clientId] });
        const found = res.clients[0] ?? null;
        if (found) set({ clients: mergeClients([found], get().clients) });
        return found;
      } catch {
        return null;
      }
    },

    async hydrate({ userId, query = {} }) {
      set({ loading: true, error: null, userId });
      try {
        const summary = await listKpiClientsSummary();
        let clients = summary.clients;
        const stored = readKpiSelection(userId);
        // T19 — bildirim linki yalnız `?setId=` taşır (`/clients/kpi/ozet?setId=…`): sahibi
        // setin kendisinden okunur (özet artık sayfalı — listede olmayabilir).
        const ownerOfSet =
          query.setId && !query.clientId
            ? await getKpiSet(query.setId)
                .then((r) => r.set.clientId)
                .catch(() => undefined)
            : undefined;
        if (ownerOfSet) query = { ...query, clientId: ownerOfSet };
        const candidate = query.clientId ?? stored.clientId;
        // Aday ilk 50'de değilse tekil özetle tamamlanır — aksi halde seçim sessizce silinirdi.
        if (candidate && !clients.some((c) => c.id === candidate)) {
          const extra = await listKpiClientsSummary({ ids: [candidate] }).catch(() => null);
          if (extra?.clients[0]) clients = mergeClients(extra.clients, clients);
        }
        const setsByClient: Record<string, { id: string }[]> = {};
        let sets: KpiSetWithCount[] = [];
        if (candidate && clients.some((c) => c.id === candidate)) {
          sets = await listKpiSets(candidate);
          setsByClient[candidate] = sets;
        }
        const sel = resolveKpiHydration({ query, stored, clients, setsByClient });
        set({
          clients,
          clientsTotal: summary.total,
          sets: sel.clientId ? sets : [],
          clientId: sel.clientId,
          setId: sel.setId,
          years: initialYearsFor(userId, sets, sel.setId),
          grid: null,
          fill: null,
        });
        persist({ userId, clientId: sel.clientId, setId: sel.setId, years: get().years });
        if (sel.setId) await get().refreshGrid();
      } catch (err) {
        set({ error: err instanceof Error ? err.message : KPI_COPY.store.loadFailed });
      } finally {
        set({ loading: false });
      }
    },

    async selectClient(clientId) {
      // P2-2: aynı müşteriye dönüşte son kullanılan set (localStorage) tercih edilir; yoksa ilk set.
      const { userId } = get();
      const stored = userId ? readKpiSelection(userId) : { clientId: null, setId: null };
      const preferred = clientId && stored.clientId === clientId ? stored.setId : null;
      set({ clientId, setId: null, sets: [], grid: null, fill: null, cycle: null, error: null });
      persist({ ...get(), clientId, setId: null });
      if (!clientId) return;
      try {
        // Picker'dan ilk 50 dışı bir müşteri seçildiyse özet listesine eklenir (ekran başlığı,
        // içe aktarma alt başlığı `clients.find` ile okur).
        await get().ensureClient(clientId);
        const sets = await listKpiSets(clientId);
        if (get().clientId !== clientId) return; // bu arada başka müşteri seçildi
        set({ sets });
        const pick = pickSetForClient(sets, preferred);
        if (pick) await get().selectSet(pick);
      } catch (err) {
        set({ error: err instanceof Error ? err.message : KPI_COPY.store.setsFailed });
      }
    },

    async selectSet(setId) {
      const { userId, sets } = get();
      set({
        setId,
        years: initialYearsFor(userId, sets, setId),
        grid: null,
        fill: null,
        cycle: null,
        error: null,
        staleConflict: null,
      });
      persist({ ...get(), setId });
      if (setId) await get().refreshGrid();
    },

    async setYears(years) {
      set({ years: [...years].sort((a, b) => a - b) });
      persist(get()); // yıl aralığı da hatırlanır
      if (get().setId) await get().refreshGrid();
    },

    async reloadSets() {
      const { clientId } = get();
      if (!clientId) return;
      const sets = await listKpiSets(clientId);
      set({ sets });
    },

    async refreshGrid() {
      const { setId, years, entityIds } = get();
      if (!setId) return;
      set({ gridLoading: true, error: null });
      try {
        const res = await getKpiResolved(setId, {
          years,
          ...(entityIds.length > 0 ? { entityIds } : {}),
        });
        set({ grid: res.grid, fill: res.fill, cycle: res.cycle });
      } catch (err) {
        set({ error: err instanceof Error ? err.message : KPI_COPY.grid.loadFailed });
      } finally {
        set({ gridLoading: false });
      }
    },

    async saveCell(args) {
      return (await writeCell(args, true)) === "ok";
    },

    saveCells(items) {
      return writeBatch(items);
    },

    retryFailed() {
      // güvenlik/UX taraması 2026-10-08 (U-H3): yazılan girdiler İLK yazımın birimiyle yeniden
      // gönderilir; başarısızlar `failedInputs`'ta kalır (writeCell yeniden yazar), tek refreshGrid.
      return writeBatch(Object.values(get().failedInputs));
    },

    async discardFailed() {
      set({ saveErrors: {}, failedInputs: {} });
      await get().refreshGrid();
    },

    async acknowledgeStale(refresh) {
      set({ staleConflict: null });
      if (refresh) await get().refreshGrid();
    },

    clearSelection() {
      set({ clientId: null, setId: null, sets: [], grid: null, fill: null, cycle: null });
      persist({ ...get(), clientId: null, setId: null });
    },
  };
});
